export const pagesPayrollCoreEn = {
  archiveScreen: {
    description: 'Archived operation packages are shown here.',
    title: 'Archive'
  },
  configScreen: {
    kinds: {
      operationEvents: 'Business Operation Events',
      operationPackages: 'Operation packages'
    }
  },
  dashboard: {
    comparedToLastMonth: 'vs last month',
    gantt: {
      barLabels: {
        accountingPending: 'Accounting sync (scheduled)',
        attendance700: 'Attendance data: 700/700',
        businessTasks: 'Business tasks: 3/4+',
        closingPending: 'Monthly close (scheduled)',
        confirm: 'Confirm',
        operationActivity: 'Operation activity: 20/40',
        paymentPending: 'Payment (scheduled)',
        payrollPending: 'Payroll (scheduled)'
      },
      calendar: {
        moreCount: '+{{count}} more',
        nextPeriod: 'Next',
        noEventsThisWeek: 'No items in this window.',
        previousPeriod: 'Previous',
        status: {
          done: 'Done',
          inProgress: 'In progress',
          todo: 'Not started'
        },
        taskAria: '{{status}} — {{label}} — {{date}}',
        today: 'Today'
      },
      dataView: {
        assignees: {
          '0': 'Payroll / HR operations',
          '1': 'Attendance administration',
          '2': 'Corporate accounting',
          '3': 'IT / integrations'
        },
        attention: {
          no: '—',
          yes: 'Yes'
        },
        calculationStatus: {
          calculated: 'Calculated',
          confirmed: 'Confirmed',
          draft: 'Not started',
          sent: 'Sent'
        },
        emptyFiltered: 'No rows match the current filters.',
        exportToastTitle: 'Export is not available in preview.',
        fixtures: {
          departments: {
            d1: 'Sales operations',
            d2: 'Store operations',
            d3: 'HQ administration',
            d4: 'Back office'
          },
          employees: {
            e1: 'Aoyama, Hanako',
            e2: 'Sato, Taro',
            e3: 'Nakamura, Yuki',
            e4: 'Ito, Ken',
            e5: 'Fujita, Mei'
          }
        },
        itemCategories: {
          deduction: 'Deduction',
          earning: 'Earning'
        },
        itemNames: {
          base: 'Base salary',
          commute: 'Commuting allowance',
          employmentInsurance: 'Employment insurance',
          healthInsurance: 'Health insurance',
          housing: 'Housing allowance',
          incomeTax: 'Income tax',
          overtime: 'Overtime allowance',
          pension: 'Pension insurance',
          position: 'Position allowance',
          residentTax: 'Resident tax'
        },
        rowKinds: {
          event: 'Event',
          step: 'Step'
        },
        scopes: {
          department: 'Department',
          employee: 'Employee',
          item: 'Item'
        },
        searchPlaceholder: 'Search events, steps, or assignees',
        searchPlaceholderPayroll: 'Search employee, item, or department',
        tabs: {
          all: 'All',
          attention: 'Needs attention',
          done: 'Done',
          inProgress: 'In progress'
        }
      },
      eventTitles: {
        bonus: 'Summer bonus run: June payment',
        fulltimeNovember: 'Payroll run: Full-time_Monthly (standard) – June',
        fulltimeOctober: 'Payroll run: Full-time_Monthly (standard) – May',
        managerOctober: 'Payroll run: Manager_Monthly (role) – May'
      },
      loadError: 'Could not load the dashboard.',
      steps: {
        accountingSync: 'Post to accounting system',
        monthlyClose: 'Monthly close',
        payment: 'Payment processing',
        payrollCalc: 'Payroll calculation',
        personnelChanges: 'Apply personnel changes & submissions',
        reflectAttendance: 'Apply attendance results',
        retroPay: 'Apply retroactive / variable allowances & deductions'
      },
      taskColumn: {
        filterAria: 'Filter task list',
        title: 'Business events & steps'
      },
      toolbar: {
        assignee: 'Assignee',
        assigneeSelf: 'Me',
        calendarPlacementByEvent: 'By event',
        calendarPlacementDeadline: 'By deadline',
        calendarPlacementLabel: 'Display',
        granularity: {
          month: 'Monthly',
          quarter: 'Quarterly',
          week: 'Weekly'
        },
        settingsAria: 'Gantt display settings',
        viewCalendar: 'Calendar',
        viewData: 'Data',
        viewGantt: 'Gantt'
      }
    },
    noMetrics: 'No metrics data available',
    startMockOperationEvent: {
      dialogTitle: 'Select payroll calculation framework and execution pattern',
      patternStep: {
        cards: {
          offCycle: {
            description: 'Applies payroll changes outside the standard monthly close, such as retroactive pay or mid-cycle corrections.',
            title: 'Off-cycle adjustment'
          },
          regular: {
            description: 'Runs calculation, confirmation, and payout steps for the selected payroll period in the usual monthly sequence.',
            title: '{{period}} — Regular monthly close'
          }
        },
        label: 'Execution pattern',
        loading: 'Loading patterns…'
      },
      startNew: 'Start new payroll',
      structureStep: {
        label: 'Payroll calculation framework',
        loading: 'Loading payroll calculation frameworks…'
      }
    },
    title: 'Dashboard'
  },
  headerActions: {
    helpAria: 'Help',
    startNew: 'Start new'
  },
  operationEvents: {
    compare: {
      addVersion: 'Add a version',
      addVersionEmpty: 'Every version is already shown',
      changedFromRight: 'Differs from the version on the right',
      currentVersion: 'Current version',
      empty: 'There is no version to compare.',
      itemColumn: 'Payroll item',
      removeVersion: 'Remove from the comparison',
      sections: {
        deductions: 'Deductions',
        earnings: 'Earnings',
        summary: 'Net pay'
      },
      title: 'Version comparison'
    },
    data: {
      compare: 'Compare',
      edit: 'Edit',
      filters: {
        organization: 'Organization',
        organizationPlaceholder: 'Search organizations',
        place: 'Place',
        placePlaceholder: 'Search places'
      },
      organizations: {
        corporate: 'Corporate',
        engineering: 'Engineering',
        sales: 'Sales'
      },
      payrollItems: {
        baseSalary: 'Base salary',
        commutingAllowance: 'Commuting allowance',
        employeePension: 'Employees’ pension',
        grossPay: 'Gross pay',
        healthInsurance: 'Health insurance',
        incomeTax: 'Income tax',
        netPay: 'Net pay',
        overtimeAllowance: 'Overtime allowance',
        residentTax: 'Resident tax',
        totalDeduction: 'Total deductions'
      },
      places: {
        fukuokaBranch: 'Fukuoka branch',
        osakaBranch: 'Osaka branch',
        tokyoHq: 'Tokyo head office'
      },
      searchPlaceholder: 'Search by name',
      sidebar: {
        close: 'Close side tab',
        open: 'Open side tab',
        title: 'Version'
      }
    },
    detail: {
      breadcrumbEvents: 'Events',
      description: 'Progress of the steps and tasks in this payroll event.',
      feed: {
        description: 'Pick a task from the list to open its detail.',
        title: 'No task selected'
      },
      metrics: {
        paymentDate: 'Payment date',
        progress: 'Progress',
        target: 'Target',
        targetUnit: 'employees',
        tasksUnit: 'tasks'
      },
      moreActionsAria: 'More actions',
      openTaskAria: 'Open task detail',
      sidebar: {
        allSteps: 'All operation steps',
        assigneeFilter: 'Assignee',
        close: 'Close side tab',
        open: 'Open side tab',
        searchPlaceholder: 'Filter by',
        statusFilter: 'Status',
        title: 'Steps and tasks',
        toggleStepAria: 'Toggle step tasks'
      },
      status: {
        done: 'Done',
        inProgress: 'In progress',
        notStarted: 'Not started'
      },
      stepDescription: 'description',
      steps: {
        attendanceAggregation: 'Attendance aggregation',
        dependentAndCommuteChanges: 'Dependent and commuting change requests',
        joinersAndLeavers: 'Joiners, leavers and transfers',
        overtimeAgreementCheck: 'Article 36 agreement check',
        payrollBaseDataUpdate: 'Payroll base data update',
        socialInsuranceAndTaxMaster: 'Social insurance and tax master update',
        unpaidAndAbsenceDeduction: 'Leave and absence deduction'
      },
      table: {
        assignee: 'Assignee',
        dueDate: 'Due date',
        status: 'Status',
        taskName: 'Task name'
      },
      tabs: {
        data: 'Data',
        detail: 'Detail'
      },
      task: {
        subordinate: 'Subordinate',
        target: 'target'
      },
      taskDetail: {
        approval: {
          flowTitle: 'Approval flow',
          rows: {
            grossPay: 'Gross pay',
            netPay: 'Net payment',
            payDate: 'Payment date',
            payExecutor: 'Payment executor',
            payrollPeriod: 'Payroll period',
            socialInsurance: 'Social insurance',
            targetEmployeeCount: 'Target employees',
            transferBank: 'Transfer bank',
            withholdingTax: 'Withholding income tax'
          },
          sections: {
            deductions: 'Deductions',
            paymentDetails: 'Payment Details',
            paymentSummary: 'Payment Summary',
            transferInfo: 'Transfer Info'
          }
        },
        assignSomeoneElse: 'Assign someone else',
        assigneeLabel: 'Assignee',
        completedToast: 'Task completed',
        confirm: 'Confirm',
        description: 'description',
        dueDateLabel: 'Due date',
        employeeCount: '{{count}} employees',
        form: {
          fieldLabel: 'Label',
          placeholder: 'Placeholder',
          validationError: 'Something went wrong...'
        },
        kinds: {
          approval: 'Approval: Payroll transfer execution',
          form: 'Task with simple form input',
          table: 'Task that requires table input'
        },
        setReminder: 'Set Reminder',
        table: {
          employee: 'Employee',
          organizationFilter: 'Organization',
          relatedColumn: 'Related item of the unit/task process',
          searchPlaceholder: 'Search by name...',
          workplaceFilter: 'Workplace'
        }
      },
      tasks: {
        x: 'Task x',
        y: 'Task y',
        z: 'Task z'
      },
      views: {
        feed: 'Feed',
        list: 'List'
      }
    },
    edit: {
      emptyFields: 'No item matches the selected conditions',
      emptyRecords: 'No employee matches',
      errorColumn: 'Error detail',
      fieldSearchPlaceholder: 'Search items',
      filters: {
        error: 'Errors',
        fixed: 'Fixed',
        noChange: 'No change'
      },
      issues: {
        baseSalaryMissing: 'Base salary is required',
        incomeTaxNegative: 'Income tax cannot be negative',
        overtimeExceedsLimit: 'Overtime allowance exceeds the limit'
      },
      noChangeBanner: 'This employee has no change from the previous version, so it is skipped when saving.',
      noRecordSelected: 'Select an employee from the list on the left',
      notes: {
        edited: 'Edited',
        fixed: 'Fixed (revalidated on save)'
      },
      readOnlyItem: 'Calculated automatically',
      save: 'Save changes',
      saved: 'Saved',
      searchPlaceholder: 'Search by name',
      sections: {
        deductions: 'Deductions',
        earnings: 'Earnings',
        summary: 'Totals'
      },
      toggleEditAria: 'Toggle editing',
      views: {
        profile: 'Profile',
        table: 'Table'
      }
    },
    instanceEmptyState: {
      description: 'Use “Start new” to start a business event.',
      title: 'No business events'
    },
    patternEmptyState: {
      description: 'No patterns are registered for this business event yet.',
      title: 'No patterns'
    },
    patterns: {
      fullTimeMonthlyPay: 'Monthly pay for full-time employees',
      partTimeMonthlyPay: 'Monthly pay for part-time',
      standardBonus: 'Standard'
    },
    sidebar: {
      bonusRun: 'Bonus run',
      payrollRun: 'Payroll run',
      yearEndTaxAdjustment: 'Year-end tax adjustment'
    },
    table: {
      eventName: 'Business Operation Event',
      progress: 'Progress'
    }
  },
  operationPackages: {
    assignTaskToPerson: {
      anchorDate: 'Anchor date',
      assignSuccess: 'Operation package started',
      assignSuccessMessage: '{{count}} people assigned',
      selectPackage: 'Select operation package',
      selectPackageDescription: 'Select an operation package to start.',
      selectPackagePattern: 'Select operation package pattern',
      selectPackagePatternDescription: 'Select a pattern to start.',
      selectPerson: 'Select person',
      selectPersonDescription: 'Select people to assign.',
      selectPersonPlaceholder: 'Select person',
      selectedOperationPackage: 'Selected operation package',
      selectedOperationPattern: 'Selected operation pattern',
      selectedPerson: 'Selected person',
      startOperationTask: 'Start',
      startOperationTaskDescription: 'Pick an operation package and pattern, then assign people.',
      validationErrors: {
        noPersonSelected: 'Select at least one person to assign.',
        pastDateWithNewPerson: 'A past date cannot be selected when a new target exists.'
      }
    },
    categories: {
      bonus_processing: 'Bonus Processing',
      mid_year_adjustment: 'Mid-Year Adjustment',
      monthly_close: 'Monthly Close',
      offboarding: 'Offboarding',
      onboarding: 'Onboarding',
      year_end: 'Year-End'
    },
    displayDataAggregate: 'View: Aggregated data',
    filterByPerson: 'Filter by person',
    mockPatternMutation: {
      description: 'Adding patterns is disabled in the demo database.',
      title: 'Demo mode'
    },
    noPackages: 'No operation packages available',
    patternEmptyState: {
      description: 'Register a pattern from the "Add operation package pattern" button.',
      title: 'No operation package patterns'
    },
    patternTabKeys: {
      default: 'All'
    },
    sideTab: {
      underDevelopment: 'This operation package is under development.'
    },
    sidebar: {
      bonusCalculation: 'Bonus calculation',
      bonusPayment: 'Bonus payment',
      fbDataProcessing: 'FB data processing',
      legalRecordSummary: 'Legal record summary',
      paySlipDataProcessing: 'Pay slip data processing',
      paySlipIssuance: 'Issue pay slips',
      payrollCalculation: 'Payroll calculation',
      payrollPayment: 'Payroll payment',
      payrollPaymentReport: 'Payroll payment report',
      residentTaxUpdate: 'Resident tax update',
      retroactiveCalculation: 'Retroactive calculation',
      withholdingSlipIssuance: 'Issue withholding slips'
    },
    startNew: 'Start new',
    status: {
      approval: 'Approval',
      approvalRequired: 'Approval required',
      attachment: 'Attachment',
      completed: 'Completed',
      notRequired: 'Not required',
      todo: 'Upstream task pending'
    },
    steps: '{{count}} steps',
    tabs: {
      archive: 'Archive',
      config: 'Config',
      operationPackages: 'Operation packages',
      settings: 'Settings'
    },
    taskEmptyState: {
      description: 'Start an operation package from the "Start new" button.',
      title: 'No operation tasks'
    },
    title: 'Operation Packages',
    types: {
      attendance: 'Attendance',
      payroll: 'Payroll',
      payroll_attendance: 'Payroll & Attendance'
    }
  },
  pageTitle: 'Payroll core',
  selectTargets: {
    clearDepartmentFilter: 'Clear department filters',
    combinedConditions: 'Conditions: {{structureName}} · {{patternName}}',
    departmentFilter: 'Department',
    departmentFilterHint: 'Filter demo people by department (Dexie seed in mock DB).',
    missingSessionDescription: 'Go to Payroll Core → Business events and tap "Start new payroll", then choose structure and pattern.',
    missingSessionTitle: 'Session data missing',
    mockStartedToast: 'Mock event started ({{structureName}} / {{patternName}}, {{count}} selected).',
    searchPlaceholder: 'Search by name or department',
    title: '{{structureName}} — {{patternName}} — Select targets'
  },
  tabs: {
    archive: 'Archive',
    config: 'Config',
    dashboard: 'Dashboard',
    operationEvents: 'Business Operation Events',
    operationPackages: 'Operation packages'
  }
};